import { useCallback, useMemo } from 'react';
import {
  ICopilotRepositoryContextValue,
  IServerContext,
  RecommendationType,
  RecommendationError,
  AssetType,
  OperatedType,
  IPerfMarker,
  ThemesAssetsRecommendationData,
  AssetsRecommendationData,
  TextAssetRecommendation,
  DisplayAdsTemplateDefinitionResponse,
} from '../types';
import { CopilotRepository } from '../components/CopilotRepository';

/**
 * Hook that provides the value for CopilotRepositoryContext. Provides functions to fetch recommended themes and assets, formats the responses, and
 * throws errors if no recommendations are returned.
 * @param {Object} serverContext - Server context with current scope and small token.
 * @param {Number} [campaignId] - The campaign ID to fetch recommendations for.
 * @param {string} finalUrl - Final URL.
 * @param {string} recommendationType - Campaign type hint given to the recommendation service so it knows what type of assets to generate.
 * @param {Object} perfMarker - Marker used to measure fetch performance.
 * @param {boolean} enableLogoAndCTA - Whether to enable logo and CTA in the recommendation.
 * @returns {Object} - Context value for CopilotRepositoryContext.
 */
export function useCopilotRepository(
  serverContext: IServerContext,
  campaignId: number | undefined,
  finalUrl: string,
  recommendationType: RecommendationType,
  perfMarker?: IPerfMarker,
  enableLogoAndCTA?: boolean,
  useClipchamp?: boolean,
) {
  const copilotRepository = useMemo(() => CopilotRepository({
    serverContext, campaignId, enableLogoAndCTA, useClipchamp,
  }), [campaignId, serverContext]);

  const { getIsCustomerIdInExclusionList } = copilotRepository;

  const fetchThemes = useCallback((userPrompt: string) => new Promise<ThemesAssetsRecommendationData>((resolve, reject) => {
    copilotRepository.getMappedThemeAssetsRecommendation(finalUrl, new Array(userPrompt))
      .then((res) => {
        if (!res.Themes || res.Themes.length === 0) {
          reject(new Error(RecommendationError.NO_RECOMMENDATION));
          return;
        }

        resolve(res);
      })
      .catch(err => reject(err));
  }), [copilotRepository, finalUrl]);

  const fetchAssets = useCallback((userPrompt?: string, themes?: string[]) => new Promise<AssetsRecommendationData>((resolve, reject) => {
    const prompts = userPrompt ? [userPrompt] : undefined;
    copilotRepository.getMappedAssetsRecommendation(finalUrl, recommendationType, prompts, themes)
      .then((res) => {
        const themesList = res.RecommendedAssets?.Themes;
        if (themesList && themesList.length > 0) {
          resolve(res);
          return;
        }
        reject(new Error(RecommendationError.NO_RECOMMENDATION));
      })
      .catch(err => reject(err));
  }), [copilotRepository, finalUrl, recommendationType]);

  const cancelFetchThemesJob = useCallback(() => {
    copilotRepository.stopGetThemeAssetsRecommendation();
    copilotRepository.stopGetAssetsRecommendation();
  }, [copilotRepository]);

  const fetchRecommendationAssets = useCallback((
    assetType: AssetType,
    operatedType: OperatedType,
    shortHeadlines: string[],
    longHeadlines: string[],
    descriptions: string[],
    selectedIndex: number,
    prompts: string[] = [],
    tones: string[] = [],
  ) => new Promise((resolve, reject) => {
    let refineTextPromise;

    switch (operatedType) {
      case OperatedType.TextRewriteWithDescription:
        refineTextPromise = copilotRepository.rewriteTextAsset(
          finalUrl,
          assetType,
          shortHeadlines,
          longHeadlines,
          descriptions,
          selectedIndex,
          prompts,
        );
        break;
      case OperatedType.TextRewriteWithTone:
        refineTextPromise = copilotRepository.tryDifferentToneTextAsset(
          finalUrl,
          assetType,
          shortHeadlines,
          longHeadlines,
          descriptions,
          selectedIndex,
          tones,
        );
        break;
      case OperatedType.TextFindSimilarText:
        refineTextPromise = copilotRepository.findSimilarTextAsset(
          finalUrl,
          assetType,
          shortHeadlines,
          longHeadlines,
          descriptions,
          selectedIndex,
        );
        break;
      default:
        reject(new Error(RecommendationError.NO_RECOMMENDATION));
        break;
    }
    if (refineTextPromise) {
      refineTextPromise
        .then((res: any) => {
          const texts = res.TextAssets as TextAssetRecommendation[];
          if (texts && texts.length > 0) {
            const assets = texts.map((asset: any) => ({ ...asset, text: asset.Text }));
            resolve(assets);
            return;
          }
          reject(new Error(RecommendationError.NO_RECOMMENDATION));
        })
        .catch(err => reject(err));
    }
  }), [copilotRepository, finalUrl, recommendationType]);

  const cancelFetchRecommendationAssetsJob = useCallback(() => {
    copilotRepository.stopRefineTextAsset();
  }, [copilotRepository]);

  const fetchDisplayAdsAssetRecommendations = useCallback((maxAdsCount?: number, brandKit?: any, templateVersion?: string) => new Promise((resolve, reject) => {
    copilotRepository.getMappedDisplayAdsRecommendation(finalUrl, maxAdsCount, brandKit, templateVersion)
      .then((res) => {
        if (res.Ads.length > 0) {
          resolve(res);
          return;
        }
        reject(new Error(RecommendationError.NO_RECOMMENDATION));
      })
      .catch(err => reject(err));
  }), [copilotRepository, finalUrl]);

  const editDisplayAdsAssetRecommendations = useCallback((ads: any) => new Promise((resolve, reject) => {
    copilotRepository.editMappedDisplayAdsRecommendation(ads)
      .then((res) => {
        if (res) {
          resolve(res);
          return;
        }
        reject(new Error(RecommendationError.NO_RECOMMENDATION));
      })
      .catch(err => reject(err));
  }), [copilotRepository]);

  const fetchDisplayAdsTemplateDefinition: (templateId: string) => Promise<DisplayAdsTemplateDefinitionResponse> = useCallback((templateId: string) => new Promise((resolve, reject) => {
    copilotRepository.getDisplayAdsTemplateDefinition(templateId)
      .then((res) => {
        if (res) {
          resolve(res);
          return;
        }
        reject(new Error(RecommendationError.NO_RECOMMENDATION));
      })
      .catch(err => reject(err));
  }), [copilotRepository]);

  const fetchDisplayAdsTemplateGroups = useCallback(() => new Promise((resolve, reject) => {
    copilotRepository.getDisplayAdsTemplateGroups()
      .then((res) => {
        if (res) {
          resolve(res);
          return;
        }
        reject(new Error(RecommendationError.NO_RECOMMENDATION));
      })
      .catch(err => reject(err));
  }), [copilotRepository]);

  const fetchDisplayAdsTemplateGroupDetails = useCallback((templateGroupId: string[], version: string) => new Promise((resolve, reject) => {
    copilotRepository.getDisplayAdsTemplateGroupDetails(templateGroupId, version)
      .then((res) => {
        if (res) {
          resolve(res);
          return;
        }
        reject(new Error(RecommendationError.NO_RECOMMENDATION));
      })
      .catch(err => reject(err));
  }), [copilotRepository]);

  const fetchVideoAdsAssetRecommendations = useCallback((maxAdsCount?: number, mock?: boolean, recType?: string, templateFilter?: any, brandKit?: any) => new Promise((resolve, reject) => {
    copilotRepository.getVideoAssetsRecommendation(finalUrl, maxAdsCount, mock, recType, templateFilter, brandKit)
      .then((res) => {
        if (res && res.data && res.data.ads && res.data.ads.length > 0) {
          resolve(res);
          return;
        }
        reject(new Error(RecommendationError.NO_RECOMMENDATION));
      })
      .catch(err => reject(err));
  }), [copilotRepository, finalUrl]);

  const editVideoAdsAssetRecommendations = useCallback((ads: any, mock?: boolean, styles?: any) => new Promise((resolve, reject) => {
    copilotRepository.editVideoAssetRecommendation(ads, mock, styles)
      .then((res) => {
        if (res) {
          resolve(res);
          return;
        }
        reject(new Error(RecommendationError.NO_RECOMMENDATION));
      })
      .catch(err => reject(err));
  }), [copilotRepository]);

  const fetchVideoKeyFrameRecommendations = useCallback((scene: any, mock?: boolean) => new Promise((resolve, reject) => {
    copilotRepository.getVideoKeyFrameRecommendation(scene, mock)
      .then((res) => {
        if (res) {
          resolve(res);
          return;
        }
        reject(new Error(RecommendationError.NO_RECOMMENDATION));
      })
      .catch(err => reject(err));
  }), [copilotRepository]);

  const ret = useMemo<ICopilotRepositoryContextValue>(() => ({
    finalUrl,
    fetchThemes,
    fetchAssets,
    cancelFetchThemesJob,
    fetchRecommendationAssets,
    fetchDisplayAdsAssetRecommendations,
    editDisplayAdsAssetRecommendations,
    fetchDisplayAdsTemplateDefinition,
    fetchDisplayAdsTemplateGroups,
    fetchDisplayAdsTemplateGroupDetails,
    fetchVideoAdsAssetRecommendations,
    editVideoAdsAssetRecommendations,
    fetchVideoKeyFrameRecommendations,
    cancelFetchRecommendationAssetsJob,
    getIsCustomerIdInExclusionList,
    perfMarker,
  }), [
    finalUrl,
    fetchThemes,
    fetchAssets,
    cancelFetchThemesJob,
    fetchRecommendationAssets,
    fetchDisplayAdsAssetRecommendations,
    editDisplayAdsAssetRecommendations,
    fetchDisplayAdsTemplateDefinition,
    fetchDisplayAdsTemplateGroups,
    fetchDisplayAdsTemplateGroupDetails,
    fetchVideoAdsAssetRecommendations,
    editVideoAdsAssetRecommendations,
    fetchVideoKeyFrameRecommendations,
    cancelFetchRecommendationAssetsJob,
    getIsCustomerIdInExclusionList,
    perfMarker,
  ]);

  return ret;
}
